import { useEffect, useState } from 'react';
import { apiClient } from '../../lib/api';
import { ReplyItem } from '../../types/accounts';
import { Spinner } from '../ui';
import { ReplyRow } from './ReplyRow';

const BUCKETS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'genuine', label: '🟢 Genuine' },
  { key: 'auto', label: '💤 OOO/Auto' },
  { key: 'unsub', label: '🚫 Unsub' },
  { key: 'reaction', label: '👍 Reaction' },
];

export function RepliesView() {
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [bucket, setBucket] = useState<string>('genuine');
  const [rows, setRows] = useState<ReplyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiClient
      .getAdminReplies(unreadOnly)
      .then(setRows)
      .catch(() => setError('Failed to load replies'))
      .finally(() => setLoading(false));
  }, [unreadOnly]);

  const counts = rows.reduce<Record<string, number>>((acc, r) => {
    const c = r.classification || 'unknown';
    acc[c] = (acc[c] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});

  const visible = bucket === 'all' ? rows : rows.filter((r) => (r.classification || 'unknown') === bucket);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">Replies</h2>
          <p className="text-sm text-neutral-500 mt-0.5">LinkedIn replies across all clients · newest first</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-neutral-600">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} className="rounded border-neutral-300" />
          Unread only
        </label>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {BUCKETS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setBucket(key)}
            className={`px-3 py-1 rounded text-xs font-medium transition ${
              bucket === key
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            {label} ({counts[key] ?? 0})
          </button>
        ))}
      </div>

      {loading ? (
        <Spinner size="md" center text="Loading replies..." />
      ) : error ? (
        <p className="text-sm text-error-600">{error}</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-neutral-500">
          {bucket === 'all'
            ? unreadOnly
              ? 'No unread replies. 🎉'
              : 'No replies yet.'
            : `No ${unreadOnly ? 'unread ' : ''}${bucket} replies.`}
        </p>
      ) : (
        <div className="space-y-2">
          {visible.map((r, i) => (
            <ReplyRow
              key={`${r.reply_key}-${i}`}
              reply={r}
              showClient
              onRead={(key) => {
                if (unreadOnly) setRows((prev) => prev.filter((x) => x.reply_key !== key));
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
